<template>
  <el-row :gutter="20">
    <el-col :md="16">
      <el-card
        v-loading="isLoading"
        class="template-card"
        shadow="never"
      >
        <template #header>
          <div class="template-card__header">
            <div>
              <h2>Event Templates</h2>
              <p>Configure customer notifications for each shipment event.</p>
            </div>
          </div>
        </template>

        <el-tabs
          v-model="activeEvent"
          class="event-tabs"
        >
          <el-tab-pane
            v-for="event in events"
            :key="event.name"
            :label="event.label"
            :name="event.name"
          >
            <el-form v-if="templates && event.name === activeEvent">
              <el-form-item label="Subject Line">
                <el-input
                  v-model="templates[event.subject]"
                  data-test="email-subject"
                  placeholder="Email subject"
                />
              </el-form-item>
              <br>
              <el-form-item label="Message">
                <div class="message-workspace">
                  <section class="message-panel">
                    <div class="message-panel__header">
                      <span>Email Content</span>
                      <EditorMenuBar
                        v-slot="{ commands }"
                        :editor="editor"
                      >
                        <el-button-group>
                          <el-button
                            size="mini"
                            title="Bold"
                            @click.native="commands.bold"
                          >
                            <FontAwesomeIcon :icon="[ 'far', 'bold' ]" />
                          </el-button>
                          <el-button
                            size="mini"
                            title="Italic"
                            @click.native="commands.italic"
                          >
                            <FontAwesomeIcon :icon="[ 'far', 'italic' ]" />
                          </el-button>
                          <el-button
                            size="mini"
                            title="Underline"
                            @click.native="commands.underline"
                          >
                            <FontAwesomeIcon :icon="[ 'far', 'underline' ]" />
                          </el-button>
                          <el-button
                            size="mini"
                            title="Bulleted list"
                            @click.native="commands.bullet_list"
                          >
                            <FontAwesomeIcon :icon="[ 'far', 'list-ul' ]" />
                          </el-button>
                          <el-button
                            size="mini"
                            title="Numbered list"
                            @click.native="commands.ordered_list"
                          >
                            <FontAwesomeIcon :icon="[ 'far', 'list-ol' ]" />
                          </el-button>
                        </el-button-group>
                      </EditorMenuBar>
                    </div>
                    <EditorContent
                      :editor="editor"
                      class="event-template-editor"
                      data-test="email-message"
                    />
                  </section>

                  <section class="message-panel">
                    <div class="message-panel__header">
                      <span>Preview</span>
                    </div>
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                      class="template-preview"
                      :class="{ 'template-preview--empty': !previewContent }"
                      v-html="previewContent"
                    />
                  </section>
                </div>
              </el-form-item>

              <el-form-item label="SMS Message">
                <el-input
                  v-model="templates[event.sms]"
                  data-test="sms-message"
                  type="textarea"
                  :rows="5"
                  placeholder="SMS message"
                />
              </el-form-item>
            </el-form>
          </el-tab-pane>
        </el-tabs>

        <div class="template-actions">
          <el-button
            plain
            :loading="isGeneratingPreview"
            :disabled="!templates"
            @click="preview"
          >
            Preview
          </el-button>
          <el-button
            type="primary"
            :loading="isSaving"
            :disabled="!templates"
            @click="saveTemplates"
          >
            Save
          </el-button>
        </div>
      </el-card>
    </el-col>

    <el-col :md="8">
      <el-card
        class="placeholder-card"
        shadow="never"
      >
        <template #header>
          <div class="placeholder-card__header">
            <span>Available Placeholders</span>
            <span>{{ placeholders.length }}</span>
          </div>
        </template>
        <div class="placeholder-list">
          <div
            v-for="placeholder in placeholders"
            :key="placeholder.token"
            class="placeholder-row"
          >
            <span class="placeholder-token">
              {{ placeholder.token }}
            </span>
            <span>
              {{ placeholder.label }}
            </span>
          </div>
        </div>
      </el-card>
    </el-col>
  </el-row>
</template>
<script>
/* eslint-disable */
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import { Bold, BulletList, Italic, ListItem, OrderedList, Underline } from 'tiptap-extensions'
import Request, { POST } from '@/plugins/sdk/Request'

export default {
  name: 'TeamNotificationTemplates',
  components: {
    EditorContent,
    EditorMenuBar
  },
  data () {
    return {
      templates: null,
      editor: null,
      activeEvent: 'despatch',
      isLoading: true,
      isSaving: false,
      isGeneratingPreview: false,
      previewContent: '',
      events: [
        { name: 'despatch', label: 'Despatch', subject: 'despatch_template_subject', email: 'despatch_template', sms: 'sms_despatch_template' },
        { name: 'in_transit', label: 'In Transit', subject: 'in_transit_template_subject', email: 'in_transit_template', sms: 'sms_in_transit_template' },
        { name: 'onboard', label: 'Onboard', subject: 'onboard_template_subject', email: 'onboard_template', sms: 'sms_onboard_template' },
        { name: 'error', label: 'Error', subject: 'error_template_subject', email: 'error_template', sms: 'sms_error_template' },
        { name: 'delivered', label: 'Delivered', subject: 'delivered_template_subject', email: 'delivered_template', sms: 'sms_delivered_template' }
      ],
      placeholders: [
        { token: '{Date}', label: 'Date' },
        { token: '{Carrier}', label: 'Carrier' },
        { token: '{InternalRef}', label: 'Internal Reference' },
        { token: '{Shipment}', label: 'Shipment Number' },
        { token: '{Reference}', label: 'Shipment Reference' },
        { token: '{SpecialInstructions}', label: 'Special Instructions' },
        { token: '{ManifestDate}', label: 'Manifest Date' },
        { token: '{EstimateDate}', label: 'Estimated Delivery Date' },
        { token: '{TotalItems}', label: 'Total number of items' },
        { token: '{TotalWeight}', label: 'Total Weight' },
        { token: '{TotalVolume}', label: 'Total Volume' },
        { token: '{CostCentre}', label: 'Cost Centre' },
        { token: '{ItemReferences}', label: 'Item References' },
        { token: '{ItemDescriptions}', label: 'Item Descriptions' },
        { token: '{ItemTable}', label: 'Table of Items' },
        { token: '{SenderName}', label: 'Your Business Name' },
        { token: '{SenderPhone}', label: 'Your Phone No' },
        { token: '{ReceiverAddress}', label: "Receiver's Address" },
        { token: '{ReceiverPhone}', label: "Receiver's Phone No" },
        { token: '{ReceiverName}', label: "Receiver's Name" },
        { token: '{TrackingUrl}', label: 'Tracking URL' },
        { token: '{ShortTrackingUrl}', label: 'Short Tracking URL (use for SMS)' }
      ]
    }
  },
  computed: {
    accountId () {
      return this.$route.params.accountId
    },
    teamId () {
      return this.$route.params.teamId
    },
    selectedEvent () {
      return this.events.find(event => event.name === this.activeEvent)
    }
  },
  watch: {
    activeEvent () {
      this.updateEditorContent()
      this.previewContent = ''
    }
  },
  created () {
    this.loadTemplates()
  },
  beforeDestroy () {
    if (this.editor) {
      this.editor.destroy()
    }
  },
  methods: {
    async preview () {
      this.isGeneratingPreview = true
      // https://dev.openfreight.com.au/api/v3/eventtemplates/preview/51dc0ee3-0ca8-4e48-8fec-85bbdbb2b5af/in_transit
      const request = await (Object.assign(new Request(), {
        method: POST,
        _pathname: `eventtemplates/preview/${this.templates.id}/${this.activeEvent}`
      })).build()

      const response = await fetch(request)

      if (response.ok) {
        const data = await response.json()
        if (data.template) {
          // show modal with v-html
          this.previewContent = data.template
        }
      }

      this.isGeneratingPreview = false
    },
    loadTemplates () {
      this.$sdk.team.read(this.teamId, builder => {
        builder.context = { resource: 'account', id: this.accountId }
        builder.include = [ 'event_templates' ]
      }).then(res => {
        this.templates = res.event_templates
        this.createEditor()
      }).finally(() => {
        this.isLoading = false
      })
    },
    createEditor () {
      this.editor = new Editor({
        content: this.templates[this.selectedEvent.email] || '',
        extensions: [
          new Bold(),
          new Italic(),
          new Underline(),
          new OrderedList(),
          new BulletList(),
          new ListItem()
        ],
        onUpdate: ({ getHTML }) => {
          this.templates[this.selectedEvent.email] = getHTML()
        }
      })
    },
    updateEditorContent () {
      if (this.editor && this.selectedEvent) {
        this.editor.setContent(this.templates[this.selectedEvent.email] || '')
      }
    },
    saveTemplates () {
      this.isSaving = true
      return this.templates.update(builder => {
        builder.context = { resource: 'team', id: this.teamId }
      })
        .then(() => {
          this.$message({ message: 'Successfully saved', type: 'success' })
        })
        .finally(() => {
          this.isSaving = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.template-card ::v-deep .el-card__header,
.placeholder-card ::v-deep .el-card__header {
  padding: 18px 20px;
  background: #f7f9fc;
}

.template-card__header h2 {
  margin: 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.template-card__header p {
  margin: 5px 0 0;
  color: #909399;
  font-size: 13px;
}

.event-tabs ::v-deep .el-tabs__header {
  margin-bottom: 24px;
}

.event-tabs ::v-deep .el-tabs__item {
  height: 44px;
  line-height: 44px;
  font-weight: 500;
}

.message-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.message-panel {
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
}

.message-panel__header {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  border-bottom: 1px solid #ebeef5;
  background: #f7f9fc;
  color: #606266;
  font-size: 12px;
  font-weight: 600;
}

.event-template-editor {
  min-height: 260px;
  padding: 12px;
}

.template-preview {
  min-height: 260px;
  padding: 16px;
  color: #303133;
  overflow-wrap: break-word;
}

.template-preview--empty::before {
  display: block;
  margin-top: 96px;
  color: #c0c4cc;
  content: 'Preview unavailable';
  font-size: 13px;
  text-align: center;
}

.template-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin: 24px -20px -20px;
  padding: 14px 20px;
  border-top: 1px solid #ebeef5;
  background: #f7f9fc;
}

.placeholder-card {
  position: sticky;
  top: 16px;
}

.placeholder-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.placeholder-card__header span:last-child {
  min-width: 24px;
  padding: 2px 7px;
  border-radius: 10px;
  background: #e8f3ff;
  color: #409eff;
  font-size: 12px;
  text-align: center;
}

.placeholder-list {
  display: grid;
  gap: 4px;
}

.placeholder-row {
  display: grid;
  grid-template-columns: minmax(130px, auto) 1fr;
  gap: 16px;
  padding: 8px;
  border-radius: 3px;
  color: #606266;
  font-size: 13px;

  &:hover {
    background: #f5f7fa;
  }

  .placeholder-token {
    color: #f56c6c;
    font-weight: 500;
  }
}

@media (max-width: 991px) {
  .message-workspace {
    grid-template-columns: 1fr;
  }

  .placeholder-card {
    position: static;
    margin-top: 20px;
  }
}
</style>
